// src/services/paySkyService.ts
// خدمة التعامل مع PaySky Lightbox من الواجهة

import { apiClient } from '@/services/apiClient'

export interface PaySkyInitResponse {
  MID: string
  TID: string
  AmountTrxn: string
  MerchantReference: string
  TrxDateTime: string
  SecureHash: string
}

export interface InitializePaymentParams {
  amount: number              // بالـ جنيه (EGP)
  merchantReference: string   // رقم المرجع (Donation Id مثلاً)
  onComplete?: (data: any) => void
  onError?: (error: any) => void
  onCancel?: () => void
}

// نضمن إن Lightbox موجودة على الـ window
declare global {
  interface Window {
    Lightbox?: {
      Checkout: {
        configure: any
        showLightbox: () => void
        closeLightbox: () => void
      }
    }
  }
}

class PaySkyService {
  private scriptLoaded = false
  private env: 'test' | 'prod'
  private callbackProcessed = false // Flag to track if callback has been processed
  private currentMerchantReference: string | null = null // Track current payment reference

  constructor() {
    // VITE_PAYSKY_ENV = 'prod' or 'test'
    const env = import.meta.env.VITE_PAYSKY_ENV
    this.env = env === 'prod' ? 'prod' : 'test'
  }

  private getScriptUrl() {
    return "https://red3.paysky.io:3011/LB/js/Lightbox.js";
    // Test / Prod URLs
    // (استخدمت الـ test URL اللي إنت كتبته red3)
    return this.env === 'prod'
      ? 'https://cube.paysky.io:6006/js/LightBox.js'
      : 'https://red3.paysky.io:3011/LB/js/Lightbox.js'
  }

  private loadScript(): Promise<void> {
    if (this.scriptLoaded && window.Lightbox) {
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(
        'script[data-paysky-lightbox="true"]'
      )
      if (existing) {
        this.scriptLoaded = true
        return resolve()
      }

      const script = document.createElement('script')
      script.src = this.getScriptUrl()
      script.async = true
      script.dataset.payskyLightbox = 'true'
      console.log(script);

      script.onload = () => {
        this.scriptLoaded = true
        resolve()
      }

      script.onerror = () => {
        reject(new Error('فشل تحميل سكربت PaySky Lightbox'))
      }

      document.body.appendChild(script)
    })
  }

  /**
   * ينادي Backend عشان يجيب إعدادات الـ Lightbox (MID/TID/AmountTrxn/TrxDateTime/SecureHash)
   * بعد كده يـ configure الـ Lightbox و يجهزه للعرض
   */
  async initializePayment(params: InitializePaymentParams): Promise<void> {
    await this.loadScript()

    // Reset callback flag for new payment
    this.callbackProcessed = false
    this.currentMerchantReference = params.merchantReference

    // مهم: baseURL = http://.../api
    // يبقى هنا نستخدم 'payments/paysky/init' من غير '/' في الأول
    const { data: config } = await apiClient.post<PaySkyInitResponse>(
      'PaySky/init',
      {
        amount: params.amount,
        merchantReference: params.merchantReference,
      }
    )
    console.log(window.Lightbox);
    if (!window.Lightbox || !window.Lightbox.Checkout) {
      throw new Error('PaySky Lightbox غير متوفر في النافذة')
    }

    window.Lightbox.Checkout.configure = {
      MID: config.MID,
      TID: config.TID,
      AmountTrxn: config.AmountTrxn,
      MerchantReference: config.MerchantReference,
      TrxDateTime: config.TrxDateTime,
      SecureHash: config.SecureHash,
      completeCallback: (data: any) => {
        // Process only the first callback
        if (this.callbackProcessed) {
          console.log('Callback already processed, ignoring duplicate callback')
          return
        }

        // Verify this callback is for the current payment
        if (data.MerchantReference && data.MerchantReference !== this.currentMerchantReference) {
          console.log('Callback merchant reference mismatch, ignoring')
          return
        }

        this.callbackProcessed = true
        console.log('Processing first PaySky completeCallback:', data)
        
        if (params.onComplete) {
          params.onComplete(data)
        }
      },
      errorCallback: (error: any) => {
        // Process only the first error callback
        if (this.callbackProcessed) {
          console.log('Error callback already processed, ignoring duplicate')
          return
        }

        this.callbackProcessed = true
        console.log('Processing first PaySky errorCallback:', error)
        
        if (params.onError) {
          params.onError(error)
        }
      },
      // cancelCallback commented out as requested
      // cancelCallback: () => {
      //   // Process only the first cancel callback
      //   if (this.callbackProcessed) {
      //     console.log('Cancel callback already processed, ignoring duplicate')
      //     return
      //   }

      //   this.callbackProcessed = true
      //   console.log('Processing first PaySky cancelCallback')
        
      //   if (params.onCancel) {
      //     params.onCancel()
      //   }
      // },
    }
  }

  showPaymentLightbox() {
    if (window.Lightbox?.Checkout?.showLightbox) {
      window.Lightbox.Checkout.showLightbox()
    } else {
      console.error('Lightbox غير مهيأ بعد')
    }
  }

  closePaymentLightbox() {
    if (window.Lightbox?.Checkout?.closeLightbox) {
      window.Lightbox.Checkout.closeLightbox()
    }
  }
}

export const paySkyService = new PaySkyService()
